import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import type { FormField, RepeatColumn } from "./ApiCrudSection";

// Renderiza el formulario de alta/edición desde el esquema de campos de la
// sección y devuelve los valores al enviar. Adaptado de
// Layouts/mantenimiento/components/EntityForm.tsx, añadiendo:
//   • `hint` por campo (reglas de negocio de tarifas/liquidaciones),
//   • `footer(values)` — render-prop para la vista previa en vivo del cálculo
//     del pago a entregable/día (AgroCloud#13).
// Presentacional + estado local, sin react-hook-form (idiom del módulo CRUD).
interface EntityFormProps {
  fields: FormField[];
  initial?: Record<string, unknown>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  submitLabel: string;
  footer?: (values: Record<string, string>) => React.ReactNode;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => void;
}

export default function EntityForm({
  fields,
  initial,
  themeClasses,
  isDarkMode,
  submitLabel,
  footer,
  onCancel,
  onSubmit,
}: EntityFormProps) {
  const { t } = useTranslation("common");
  const [values, setValues] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    for (const f of fields) {
      if (f.type === "repeat") {
        // Los campos `repeat` llevan un array de filas, serializado como JSON
        // para que el mapa de valores siga siendo Record<string, string>.
        const seed = initial?.[f.key];
        base[f.key] = JSON.stringify(Array.isArray(seed) ? seed : []);
        continue;
      }
      const seed = initial?.[f.key] ?? f.defaultValue ?? "";
      base[f.key] = seed === null ? "" : String(seed);
    }
    return base;
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const inputBase = `w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
    isDarkMode
      ? "bg-[#232323] border-dark-border text-white focus:border-primary-500"
      : "bg-white border-gray-300 text-gray-900 focus:border-primary-500"
  }`;

  const set = (key: string, v: string) =>
    setValues((prev) => {
      const next = { ...prev, [key]: v };
      // Campos derivados: el que cambió puede arrastrar a otros (ver `derive`).
      const derived = fields.find((f) => f.key === key)?.derive?.(v, next);
      return derived ? { ...next, ...derived } : next;
    });
  const visibleFields = fields.filter((f) => !f.visibleWhen || f.visibleWhen(values));

  const handleSubmit = async () => {
    const nextErrors: Record<string, boolean> = {};
    for (const f of visibleFields) {
      if (f.required && !values[f.key]?.trim()) nextErrors[f.key] = true;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {visibleFields.map((f) => (
          <div
            key={f.key}
            className={f.type === "textarea" || f.type === "repeat" ? "sm:col-span-2" : ""}
          >
            <label
              className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}
            >
              {f.label}
              {f.required && <span className="ml-0.5 text-red-500">*</span>}
            </label>

            {f.type === "repeat" ? (
              <RepeaterField
                columns={f.repeatColumns ?? []}
                addRowLabel={f.addRowLabel ?? t("team.addRow")}
                isDarkMode={isDarkMode}
                themeClasses={themeClasses}
                value={values[f.key]}
                onChange={(json) => set(f.key, json)}
              />
            ) : f.type === "select" ? (
              <select
                aria-label={f.label}
                value={values[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              >
                <option value="">—</option>
                {(f.optionsFor ? f.optionsFor(values) : f.options)?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea
                aria-label={f.label}
                rows={3}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            ) : (
              <input
                aria-label={f.label}
                type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            )}

            {f.hint && !errors[f.key] && (
              <p className={`mt-1 text-[11px] ${themeClasses.textSecondary}`}>{f.hint}</p>
            )}
            {errors[f.key] && (
              <p className="mt-1 text-xs text-red-500">{t("team.requiredField")}</p>
            )}
          </div>
        ))}
      </div>

      {/* Pie dinámico: vista previa del cálculo del pago (#13). */}
      {footer?.(values)}

      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          {t("team.cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-400 disabled:opacity-60"
        >
          {submitting ? t("team.loading") : submitLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor de líneas para los campos `repeat` (miembros de squad, tramos de
// precio de una tarifa). Lee y escribe el valor como array JSON serializado.
// ---------------------------------------------------------------------------
type RepeaterRow = Record<string, string | number>;

interface RepeaterFieldProps {
  columns: RepeatColumn[];
  addRowLabel: string;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  value: string;
  onChange: (json: string) => void;
}

function RepeaterField({
  columns,
  addRowLabel,
  isDarkMode,
  themeClasses,
  value,
  onChange,
}: RepeaterFieldProps) {
  const { t } = useTranslation("common");
  let rows: RepeaterRow[] = [];
  try {
    const parsed = JSON.parse(value || "[]");
    if (Array.isArray(parsed)) rows = parsed as RepeaterRow[];
  } catch {
    rows = [];
  }

  const cellBase = `w-full rounded-md border px-2 py-1.5 text-sm outline-none ${
    isDarkMode
      ? "bg-[#232323] border-dark-border text-white focus:border-primary-500"
      : "bg-white border-gray-300 text-gray-900 focus:border-primary-500"
  }`;

  const commit = (next: RepeaterRow[]) => onChange(JSON.stringify(next));

  const addRow = () => {
    const blank: RepeaterRow = {};
    columns.forEach((c) => (blank[c.key] = c.type === "number" ? 0 : ""));
    commit([...rows, blank]);
  };

  const updateCell = (i: number, key: string, raw: string, isNum: boolean) => {
    const next = rows.map((r, idx) =>
      idx === i ? { ...r, [key]: isNum ? Number(raw) || 0 : raw } : r,
    );
    commit(next);
  };

  const removeRow = (i: number) => commit(rows.filter((_, idx) => idx !== i));

  return (
    <div
      className={`rounded-lg border p-2 ${themeClasses.border} ${
        isDarkMode ? "bg-[#1c1c1c]" : "bg-gray-50"
      }`}
    >
      {rows.length === 0 ? (
        <p className={`px-1 py-2 text-xs ${themeClasses.textSecondary}`}>
          {t("team.noRows")}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex items-end gap-2">
              {columns.map((c) => (
                <div key={c.key} className="flex-1">
                  <span
                    className={`mb-0.5 block text-[10px] ${themeClasses.textSecondary}`}
                  >
                    {c.label}
                  </span>
                  {c.type === "select" ? (
                    <select
                      aria-label={c.label}
                      value={String(row[c.key] ?? "")}
                      onChange={(e) => updateCell(i, c.key, e.target.value, false)}
                      className={cellBase}
                    >
                      <option value="">{c.placeholder ?? "—"}</option>
                      {c.options?.map((o) => (
                        <option
                          key={o.value}
                          value={o.value}
                          // Ya elegido en otra fila: se ve, pero no se repite.
                          disabled={c.unique && rows.some((r, idx) => idx !== i && String(r[c.key] ?? "") === o.value)}
                        >
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={c.type === "number" ? "number" : "text"}
                      value={String(row[c.key] ?? "")}
                      onChange={(e) => updateCell(i, c.key, e.target.value, c.type === "number")}
                      className={cellBase}
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => removeRow(i)}
                title={t("team.delete")}
                className="mb-0.5 rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={addRow}
        className="mt-2 flex items-center gap-1 rounded-md border border-dashed border-primary-500/50 px-3 py-1.5 text-xs text-primary-500 hover:bg-primary-500/10"
      >
        <i className="ri-add-line" />
        {addRowLabel}
      </button>
    </div>
  );
}
